import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Network } from '@capacitor/network';
import router from './router';


import { Capacitor } from '@capacitor/core';
import { setSwipeBackEnabled } from './services/swipeBack';

export const isNativePlatform = () => {
  return window.Capacitor?.isNativePlatform() ?? false;
};


export const initCapacitor = async () => {
  // Only run on native platforms
  if (!isNativePlatform()) {
    return;
  }

  try {
    const openResetPasswordLink = async (url) => {
      try {
        const link = new URL(url);
        if (link.protocol !== 'https:' || !link.pathname.startsWith('/reset-password/')) {
          return;
        }

        await router.isReady();
        await router.push({
          path: link.pathname,
          query: Object.fromEntries(link.searchParams.entries()),
        });
      } catch (error) {
        console.error('Failed to open app link:', error);
      }
    };

    App.addListener('appUrlOpen', ({ url }) => openResetPasswordLink(url));

    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      await openResetPasswordLink(launchUrl.url);
    }

    if (Capacitor.getPlatform() === 'ios') {
      await setSwipeBackEnabled(true);
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    } else if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setStyle({ style: Style.Light });
    }
    
    // Hide splash screen after app is ready
    await SplashScreen.hide();
  
    
    // Handle back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      // Check specifically for modal state keys set by useModalHistory composable
      // (Vue Router also sets history state, so we can't just check Object.keys().length > 0)
      const state = window.history.state || {}
      const modalKeys = [
        "mobile-privacy-modal",
        "mobile-contact-modal",
        "desktop-policy-modal",
        "desktop-contact-modal",
        "qr-code-modal",
        "phone-completion-modal",
        "dashboard-transaction-modal",
        "dashboard-benefit-modal",
        "dashboard-voucher-modal",
        "dashboard-tier-upgrade-modal",
        "dashboard-all-promotions-modal",
        "desktop-transaction-modal",
        "desktop-benefit-modal",
        "desktop-voucher-modal",
        "desktop-tier-upgrade-modal",
        "desktop-all-promotions-modal",
        "desktop-qrcode-modal",
        "transaction-detail",
        "filter-modal",
        "sort-modal",
        "deals-filter-modal",
        "deals-sort-modal",
        "deals-voucher-detail",
        "voucher-filter-modal",
        "voucher-sort-modal",
        "voucher-detail-modal",
        "voucher-detail",
        "gift-voucher-modal",
        "dashboard-gift-modal",
      ];
      const hasModalState = modalKeys.some(key => state[key] === true)

      if (hasModalState) {
        // Modal is open — trigger popstate via history.back()
        // useModalHistory composable will handle closing it
        window.history.back()
        return
      }
      // No modal open, normal navigation
      if (canGoBack) {
        window.history.back()
      } else {
        App.exitApp()
      }
    });

    console.log('Capacitor initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Capacitor:', error);
  }
};

/**
 * Get current network status
 * @returns {Promise<{connected: boolean, connectionType: string}>}
 */
export const getNetworkStatus = async () => {
  return await Network.getStatus();
};

/**
 * Add listener for network status changes
 * @param {function} callback - Function to call when network status changes
 * @returns {Promise<PluginListenerHandle>}
 */
export const addNetworkListener = (callback) => {
  return Network.addListener('networkStatusChange', callback);
};

/**
 * Get app info (version, name, etc.)
 * @returns {Promise<AppInfo>}
 */
export const getAppInfo = async () => {
  if (!isNativePlatform()) {
    return {
      name: 'Membership',
      id: 'id.mymembership.app',
      build: process.env.VUE_APP_VERSION || '1.0.0',
      version: process.env.VUE_APP_VERSION || '1.0.0'
    };
  }
  return await App.getInfo();
};
